<template>
    <div class="NavTab">
        <!--
            Triggered when the nav tab is clicked.
            @event navigate
            @property {object} to An object containing a route name
        -->
        <button
            v-if="!useRouterLink"
            :class="[
                'NavTab__button',
                isMainActive || isActive
                    ? 'NavTab__button--active'
                    : 'NavTab__button--inactive',
                size !== '' && `NavTab__button--${size}`
            ]"
            :disabled="disabled"
            type="button"
            @click="$emit('navigate', to)"
        >
            <span class="NavTab__text">
                <!-- @slot Can be used instead of the `label` prop -->
                <slot>{{ label }}</slot>
            </span>
        </button>
        <LinkWrap
            v-else
            :class="[
                'NavTab__link',
                isMainActive || isActive
                    ? 'NavTab__link--active'
                    : 'NavTab__link--inactive',
                size !== '' && `NavTab__link--${size}`
            ]"
            :disabled="disabled"
            :to="to"
            :is-router-link="true"
        >
            <span class="NavTab__text">
                <slot>{{ label }}</slot>
            </span>
        </LinkWrap>
        <div
            v-if="showSubTabs"
            class="NavTab__sub-tab-container"
        >
            <RPSubTab
                v-for="subTab in subTabs"
                :key="subTab.label"
                class="NavTab__sub-tab"
                :label="subTab.label"
                :is-active="subTab.to.name === activeTab"
                :to="subTab.to"
                @navigate="navigate"
            />
        </div>
        <RPSubTab
            v-if="showDynamicTabs"
            class="NavTab__dynamic-tab"
            :label="dynamicTab"
            :is-active="true"
        />
    </div>
</template>
<script>
import RPSubTab from './RPSubTab.vue';
import LinkWrap from 'components/Basic/Misc/LinkWrap';

export default {
    name: 'RPNavTab',
    components: {
        RPSubTab,
        LinkWrap
    },
    props: {
        size: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        /**
         * An object with a `name` property matching a route name
         */
        to: {
            type: Object,
            default: () => ({name: ''}),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether this is the currently active menu item in a list.
         */
        activeTab: {
            type: String,
            default: '',
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        /**
         * A string to be displayed as a sub-item under this nav tab.
         * This is generally used to indicate the currently visible sub-item.
         * Note: Only shown when this nav tab is active (`isActive === true`)
         */
        dynamicTab: {
            type: String,
            default: ''
        },
        /**
         * An array of subtabs, with `label` and `to` properties.
         * The `to` property must be a route object with a `name` property.
         */
        subTabs: {
            type: Array,
            default: () => ([])
        },
        useRouterLink: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        showSubTabs(){
            return this.subTabs.findIndex(s => s.to.name === this.activeTab) !== -1;
        },
        isMainActive(){
            return this.activeTab === this.to.name
                    || this.showSubTabs;
        },
        showDynamicTabs(){
            return this.isMainActive
              && !this.subTabs.length
              && this.dynamicTab !== '';
        }
    },
    methods: {
        navigate(to) {
            if(this.activeTab !== to.name){
                this.$emit('navigate', to);
            }
        }
    }
};
</script>
<style lang="scss">
    .NavTab {
        &__button,
        &__link {
            background: transparent;
            height: 2em;
            width: fit-content;
            padding: 0 0.5rem;
            font-size: 1rem;
            border-radius: 4px;
            border-width: 0;
            cursor: pointer;
            display: flex;

            &--sm {
                font-size: 0.875rem;
                height: 1.5em;
            }

            &--lg {
                padding: 0.5rem 1rem;
            }

            &--inactive {
                color: $grey;
                &:focus { outline: none; }
                &:hover {
                    background: $grey-50;
                    border-radius: 4px;
                }
                &:active {
                    background: #E4F1FF;
                    color: $text-link;
                    border-radius: 4px;
                }
                &:disabled{
                    cursor: default;
                    border-radius: 4px;
                    color: $grey-100;
                    background: $grey-50;
                }
            }

            &--active {
                cursor: pointer;
                background: #E4F1FF;
                color: $text-link;
                &:focus {
                    box-shadow: 0 0 4px $highlight-blue;
                    border-radius: 4px;
                }
            }
        }

        &__link {
            text-decoration: none;
        }

        &__text{
            font-family: proxima-nova, sans-serif;
            white-space: nowrap;
            margin: auto 0;
        }

        &__sub-tab-container{
            margin-left: 1rem;
        }
        &__sub-tab {
            margin-top: 0.5rem;
        }
        &__dynamic-tab{
            margin-top: 0.25rem;
            margin-left: 1rem;
        }
    }
</style>

<docs>
The `RPNavTab` component is generally used within navbars such as `RPSidebar`, `RPTopNav`, and `RPTopbar`.

Basic example:
```vue
<RPNavTab>My Label</RPNavTab>
```

Example with subtab (see props above):
```vue
<RPNavTab
    label="Header"
    :to="{name: 'ParentRoute'}"
    activeTab="ChildRoute"
    :subTabs="[
        {label: 'Child', to: {name: 'ParentRoute'}},
        {label: 'Child Two', to: {name: 'ChildRoute'}}
    ]"
>
</RPNavTab>
```
</docs>
